import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { Paper } from '@mui/material'
import { GridCell } from '@mui/x-data-grid'

import { TableUrlPagination } from '../../../../ui'
import { USERS_TABLE_ACTIONS, USERS_TABLE_COLUMNS } from '../../constants'
import { TableActions } from '../table-actions'
import { guildUsersTableStyles } from './guild-users-table.styles'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const GuildUsersTable = () => {
  const { formatMessage } = useIntl()
  const {
    data,
    search,
    pagination: { total_usuarios: totalEntries },
    isLoading,
  } = useSelector((state) => state.guildUsers)
  const combos = useSelector((state) => state.combos)
  const formattedColumns = useMemo(
    () =>
      [...USERS_TABLE_COLUMNS, ...USERS_TABLE_ACTIONS].map((column) => {
        // Base column configuration for Material React Table
        const baseColumn = {
          accessorKey: column.field,
          id: column.field,
          size: column.width || 200,
          minSize: column.minWidth || 100,
          maxSize: column.maxWidth,
          enableSorting: column.sorting !== false,
          muiTableHeadCellProps: {
            align: column.align || 'left',
          },
          muiTableBodyCellProps: {
            align: column.align || 'left',
            sx: {
              minWidth: column.minWidth || 100,
            },
          },
        }

        // Handle action column (active field)
        if (column.field === 'active') {
          return {
            ...baseColumn,
            header: formatMessage({ id: `global.${column.field}` }),
            Cell: ({ row }) => <TableActions row={row} />,
            muiTableHeadCellProps: {
              align: 'center',
              sx: {
                ...guildUsersTableStyles.header,
              },
            },
            muiTableBodyCellProps: {
              align: 'center',
              sx: {
                ...guildUsersTableStyles.cell,
                minWidth: column.minWidth || 100,
              },
            },
          }
        }

        // Handle regular columns
        const result = {
          ...baseColumn,
          header: formatMessage({ id: `pages.guild.users.table.columns.${column.field}.title` }),
        }

        // Add lookup functionality if combo data exists
        if (
          column.hasOwnProperty('comboId') &&
          combos[column.comboId] &&
          combos[column.comboId].data
        ) {
          const lookup = getLookup(combos[column.comboId].data)
          result.Cell = ({ cell }) => {
            const value = cell.getValue()
            const lookupValue = lookup[value] || value
            return lookupValue ? <span>{lookupValue}</span> : <span>--</span>
          }
        } else {
          // Default cell renderer with empty value handling
          result.Cell = ({ cell }) => {
            const value = cell.getValue()
            if (value === null || value === undefined || value === '') {
              return <span>--</span>
            }
            return <span>{value}</span>
          }
        }

        return result
      }),
    [combos, formatMessage] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const tableOptions = useMemo(
    () => ({
      toolbar: false,
      selection: false,
      draggable: false,
      muiTableProps: {
        sx: {
          tableLayout: 'fixed',
        },
      },
    }),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  )

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={tableOptions}
      totalEntries={totalEntries}
      search={search}
      data={data}
      isLoading={isLoading}
    />
  )
}
